import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Save, Zap } from "lucide-react";
import { getApiErrorPayload, printerConfigAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { Loading } from "@/components/ui/loader";

interface PrinterConfig {
	printerName: string;
	connectionType: "browser" | "usb" | "network" | "bluetooth";
	ipAddress: string;
	port: string;
	paperWidth: number;
	copies: number;
	headerText: string;
	footerText: string;
	showLogo: boolean;
	showTax: boolean;
	showDiscount: boolean;
	fontSize: "small" | "normal" | "large";
	receiptStyle: "standard" | "compact";
	autoPrint: boolean;
	openCashDrawer: boolean;
	isActive: boolean;
}

const emptyConfig: PrinterConfig = {
	printerName: "",
	connectionType: "browser",
	ipAddress: "",
	port: "",
	paperWidth: 80,
	copies: 1,
	headerText: "",
	footerText: "",
	showLogo: true,
	showTax: true,
	showDiscount: true,
	fontSize: "normal",
	receiptStyle: "standard",
	autoPrint: true,
	openCashDrawer: false,
	isActive: true,
};

/** Branch-scoped receipt-printer configuration form. Embedded in the POS detail page. */
export function PrinterConfigManager({ branchId }: { branchId: number }) {
	const { t } = useTranslation();
	const { toast } = useToast();
	const [config, setConfig] = useState<PrinterConfig>(emptyConfig);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [testing, setTesting] = useState(false);

	const showError = useCallback(
		(error: unknown) => {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description: t(payload.key, (payload.params ?? {}) as Record<string, string>),
				variant: "destructive",
			});
		},
		[t, toast],
	);

	const fetchConfig = useCallback(() => {
		setLoading(true);
		printerConfigAPI
			.get(branchId)
			.then((data: Partial<PrinterConfig>) =>
				setConfig({
					...emptyConfig,
					...data,
					printerName: data.printerName ?? "",
					ipAddress: data.ipAddress ?? "",
					port: data.port != null ? String(data.port) : "",
					headerText: data.headerText ?? "",
					footerText: data.footerText ?? "",
				}),
			)
			.catch(showError)
			.finally(() => setLoading(false));
	}, [branchId, showError]);

	useEffect(() => {
		fetchConfig();
	}, [fetchConfig]);

	const set = <K extends keyof PrinterConfig>(key: K, value: PrinterConfig[K]) =>
		setConfig(prev => ({ ...prev, [key]: value }));

	const buildPayload = () => ({
		branchId,
		printerName: config.printerName.trim() || undefined,
		connectionType: config.connectionType,
		ipAddress: config.ipAddress.trim() || undefined,
		port: config.port.trim() ? parseInt(config.port, 10) : undefined,
		paperWidth: config.paperWidth,
		copies: config.copies,
		headerText: config.headerText,
		footerText: config.footerText,
		showLogo: config.showLogo,
		showTax: config.showTax,
		showDiscount: config.showDiscount,
		fontSize: config.fontSize,
		receiptStyle: config.receiptStyle,
		autoPrint: config.autoPrint,
		openCashDrawer: config.openCashDrawer,
		isActive: config.isActive,
	});

	const handleSave = async (e: React.FormEvent) => {
		e.preventDefault();
		setSaving(true);
		try {
			await printerConfigAPI.save(buildPayload());
			toast({ title: t("common.success"), description: t("printerConfig.messages.saved") });
			fetchConfig();
		} catch (error) {
			showError(error);
		} finally {
			setSaving(false);
		}
	};

	const handleTest = async () => {
		setTesting(true);
		try {
			// Persist first so the test reflects what is stored, then probe.
			await printerConfigAPI.save(buildPayload());
			const res = (await printerConfigAPI.test(branchId)) as { reachable: boolean; mode: string };
			toast({
				title: t("common.success"),
				description:
					res.mode === "network" ? t("printerConfig.test.reachable") : t("printerConfig.test.clientOk"),
			});
		} catch (error) {
			showError(error);
		} finally {
			setTesting(false);
		}
	};

	if (loading) return <Loading />;

	const isNetwork = config.connectionType === "network";

	return (
		<form onSubmit={handleSave} className="space-y-6">
			{/* Connection */}
			<div className="space-y-4">
				<h4 className="text-sm font-semibold text-muted-foreground">
					{t("printerConfig.sections.connection")}
				</h4>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label htmlFor="pc-name">{t("printerConfig.form.printerName")}</Label>
						<Input
							id="pc-name"
							value={config.printerName}
							onChange={e => set("printerName", e.target.value)}
							placeholder={t("printerConfig.form.printerNamePlaceholder")}
						/>
					</div>
					<div className="space-y-2">
						<Label>{t("printerConfig.form.connectionType")}</Label>
						<Select
							value={config.connectionType}
							onValueChange={v => set("connectionType", v as PrinterConfig["connectionType"])}
						>
							<SelectTrigger aria-label={t("printerConfig.form.connectionType")}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="browser">{t("printerConfig.connection.browser")}</SelectItem>
								<SelectItem value="network">{t("printerConfig.connection.network")}</SelectItem>
								<SelectItem value="usb">{t("printerConfig.connection.usb")}</SelectItem>
								<SelectItem value="bluetooth">{t("printerConfig.connection.bluetooth")}</SelectItem>
							</SelectContent>
						</Select>
					</div>
					{isNetwork ? (
						<>
							<div className="space-y-2">
								<Label htmlFor="pc-ip">{t("printerConfig.form.ipAddress")}</Label>
								<Input
									id="pc-ip"
									value={config.ipAddress}
									onChange={e => set("ipAddress", e.target.value)}
									placeholder="192.168.1.50"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="pc-port">{t("printerConfig.form.port")}</Label>
								<Input
									id="pc-port"
									type="number"
									value={config.port}
									onChange={e => set("port", e.target.value)}
									placeholder="9100"
								/>
							</div>
						</>
					) : null}
				</div>
				<p className="text-xs text-muted-foreground">{t("printerConfig.hardwareNote")}</p>
			</div>

			{/* Paper & layout */}
			<div className="space-y-4">
				<h4 className="text-sm font-semibold text-muted-foreground">{t("printerConfig.sections.paper")}</h4>
				<div className="grid gap-4 sm:grid-cols-2">
					<div className="space-y-2">
						<Label>{t("printerConfig.form.paperWidth")}</Label>
						<Select
							value={String(config.paperWidth)}
							onValueChange={v => set("paperWidth", parseInt(v, 10))}
						>
							<SelectTrigger aria-label={t("printerConfig.form.paperWidth")}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="80">80mm</SelectItem>
								<SelectItem value="58">58mm</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-2">
						<Label htmlFor="pc-copies">{t("printerConfig.form.copies")}</Label>
						<Input
							id="pc-copies"
							type="number"
							min={1}
							max={5}
							value={config.copies}
							onChange={e => set("copies", Math.min(5, Math.max(1, parseInt(e.target.value) || 1)))}
						/>
					</div>
					<div className="space-y-2">
						<Label>{t("printerConfig.form.fontSize")}</Label>
						<Select
							value={config.fontSize}
							onValueChange={v => set("fontSize", v as PrinterConfig["fontSize"])}
						>
							<SelectTrigger aria-label={t("printerConfig.form.fontSize")}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="small">{t("printerConfig.font.small")}</SelectItem>
								<SelectItem value="normal">{t("printerConfig.font.normal")}</SelectItem>
								<SelectItem value="large">{t("printerConfig.font.large")}</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-2">
						<Label>{t("printerConfig.form.receiptStyle")}</Label>
						<Select
							value={config.receiptStyle}
							onValueChange={v => set("receiptStyle", v as PrinterConfig["receiptStyle"])}
						>
							<SelectTrigger aria-label={t("printerConfig.form.receiptStyle")}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="standard">{t("printerConfig.style.standard")}</SelectItem>
								<SelectItem value="compact">{t("printerConfig.style.compact")}</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>
			</div>

			{/* Content */}
			<div className="space-y-4">
				<h4 className="text-sm font-semibold text-muted-foreground">{t("printerConfig.sections.content")}</h4>
				<div className="space-y-2">
					<Label htmlFor="pc-header">{t("printerConfig.form.headerText")}</Label>
					<Textarea
						id="pc-header"
						rows={2}
						value={config.headerText}
						onChange={e => set("headerText", e.target.value)}
						placeholder={t("printerConfig.form.headerPlaceholder")}
					/>
				</div>
				<div className="space-y-2">
					<Label htmlFor="pc-footer">{t("printerConfig.form.footerText")}</Label>
					<Textarea
						id="pc-footer"
						rows={2}
						value={config.footerText}
						onChange={e => set("footerText", e.target.value)}
						placeholder={t("printerConfig.form.footerPlaceholder")}
					/>
				</div>
				<div className="grid gap-3 sm:grid-cols-3">
					<ToggleRow
						label={t("printerConfig.form.showLogo")}
						checked={config.showLogo}
						onChange={v => set("showLogo", v)}
					/>
					<ToggleRow
						label={t("printerConfig.form.showTax")}
						checked={config.showTax}
						onChange={v => set("showTax", v)}
					/>
					<ToggleRow
						label={t("printerConfig.form.showDiscount")}
						checked={config.showDiscount}
						onChange={v => set("showDiscount", v)}
					/>
				</div>
			</div>

			{/* Behavior */}
			<div className="space-y-4">
				<h4 className="text-sm font-semibold text-muted-foreground">{t("printerConfig.sections.behavior")}</h4>
				<div className="grid gap-3 sm:grid-cols-3">
					<ToggleRow
						label={t("printerConfig.form.autoPrint")}
						checked={config.autoPrint}
						onChange={v => set("autoPrint", v)}
					/>
					<ToggleRow
						label={t("printerConfig.form.openCashDrawer")}
						checked={config.openCashDrawer}
						onChange={v => set("openCashDrawer", v)}
					/>
					<ToggleRow
						label={t("printerConfig.form.isActive")}
						checked={config.isActive}
						onChange={v => set("isActive", v)}
					/>
				</div>
			</div>

			<PermissionGate permission="settings.update">
				<div className="flex flex-wrap justify-end gap-2">
					<Button type="button" variant="outline" onClick={handleTest} disabled={testing || saving}>
						<Zap className="me-2 h-4 w-4" />
						{t("printerConfig.testPrint")}
					</Button>
					<Button type="submit" disabled={saving}>
						<Save className="me-2 h-4 w-4" />
						{t("common.save")}
					</Button>
				</div>
			</PermissionGate>
		</form>
	);
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
	return (
		<div className="flex items-center justify-between rounded-md border p-3">
			<Label className="text-sm">{label}</Label>
			<Switch aria-label={label} checked={checked} onCheckedChange={onChange} />
		</div>
	);
}
